import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {LoadingButton} from "@mui/lab";
import {
    Button,
    Card,
    CardContent,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    Stack,
    Tooltip,
    Typography,
} from "@mui/material";
import {useState} from "react";
import {useTranslation} from "react-i18next";

interface Props {
    onLogout: () => void;
}

type IndexedDbFactoryWithDatabases = IDBFactory & {
    databases?: () => Promise<Array<{name?: string}>>;
};

const clearCookies = () => {
    const pathSegments = window.location.pathname.split('/').filter(Boolean);
    const nestedPaths = pathSegments.map(
        (_, index) => `/${pathSegments.slice(0, index + 1).join('/')}`,
    );
    const paths = Array.from(new Set([
        '/',
        ...nestedPaths,
        ...nestedPaths.map(path => `${path}/`),
    ]));
    const domainAttributes = [
        '',
        `; domain=${window.location.hostname}`,
        `; domain=.${window.location.hostname}`,
    ];

    document.cookie.split(';').forEach(cookie => {
        const cookieName = cookie.split('=')[0].trim();
        if (!cookieName) return;

        paths.forEach(path => {
            domainAttributes.forEach(domainAttribute => {
                document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; Max-Age=0; path=${path}${domainAttribute}`;
            });
        });
    });
};

const clearIndexedDb = async () => {
    if (!('indexedDB' in window)) return;

    const indexedDb = window.indexedDB as IndexedDbFactoryWithDatabases;
    const databases = await indexedDb.databases?.();
    if (!databases) return;

    await Promise.allSettled(
        databases
            .map(database => database.name)
            .filter((name): name is string => !!name)
            .map(name => new Promise<void>(resolve => {
                const request = indexedDb.deleteDatabase(name);
                request.onsuccess = () => resolve();
                request.onerror = () => resolve();
                request.onblocked = () => resolve();
            })),
    );
};

const clearCacheStorage = async () => {
    if (!('caches' in window)) return;

    const cacheNames = await window.caches.keys();
    await Promise.allSettled(cacheNames.map(cacheName => window.caches.delete(cacheName)));
};

const unregisterServiceWorkers = async () => {
    if (!('serviceWorker' in navigator)) return;

    const registrations = await navigator.serviceWorker.getRegistrations();
    await Promise.allSettled(registrations.map(registration => registration.unregister()));
};

const resetLocalAppData = async () => {
    await Promise.allSettled([
        clearCacheStorage(),
        clearIndexedDb(),
        unregisterServiceWorkers(),
    ]);

    window.localStorage.clear();
    window.sessionStorage.clear();
    clearCookies();
    window.location.reload();
};

const AccountSessionActions = ({onLogout}: Props) => {
    const {t} = useTranslation();
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [resetting, setResetting] = useState(false);

    const handleReset = async () => {
        setResetting(true);
        await resetLocalAppData();
    };

    return (
        <Stack gap={2}>
            <Typography variant={'h6'} textAlign={'start'}>
                {t('Session and browser data')}
            </Typography>

            <Card variant={'outlined'}>
                <CardContent sx={{p: 0, '&:last-child': {pb: 0}}}>
                    <Stack
                        direction={{xs: 'column', sm: 'row'}}
                        alignItems={{xs: 'stretch', sm: 'center'}}
                        justifyContent={'space-between'}
                        gap={2}
                        sx={{p: 2}}
                    >
                        <Stack direction={'row'} alignItems={'center'} gap={0.5}>
                            <Typography variant={'subtitle1'} fontWeight={600} textAlign={'start'}>
                                {t('Logout')}
                            </Typography>
                            <Tooltip title={t('Sign out of Crqlar on this browser.')} arrow>
                                <IconButton
                                    size={'small'}
                                    aria-label={t('Sign out of Crqlar on this browser.')}
                                    sx={{p: 0.25, color: 'text.secondary'}}
                                >
                                    <InfoOutlinedIcon sx={{fontSize: 16}}/>
                                </IconButton>
                            </Tooltip>
                        </Stack>

                        <Button
                            variant={'contained'}
                            color={'error'}
                            onClick={onLogout}
                            sx={{alignSelf: {xs: 'flex-start', sm: 'center'}, flexShrink: 0}}
                        >
                            {t('Logout')}
                        </Button>
                    </Stack>

                    <Divider/>

                    <Stack
                        direction={{xs: 'column', sm: 'row'}}
                        alignItems={{xs: 'stretch', sm: 'center'}}
                        justifyContent={'space-between'}
                        gap={2}
                        sx={{p: 2}}
                    >
                        <Stack direction={'row'} alignItems={'center'} gap={0.5}>
                            <Typography variant={'subtitle1'} fontWeight={600} textAlign={'start'}>
                                {t('Clear Crqlar data from this browser')}
                            </Typography>
                            <Tooltip
                                title={t('Clear Crqlar settings, cookies, caches, and local databases stored in this browser. This signs you out and reloads Crqlar.')}
                                arrow
                            >
                                <IconButton
                                    size={'small'}
                                    aria-label={t('Clear Crqlar settings, cookies, caches, and local databases stored in this browser. This signs you out and reloads Crqlar.')}
                                    sx={{p: 0.25, color: 'text.secondary'}}
                                >
                                    <InfoOutlinedIcon sx={{fontSize: 16}}/>
                                </IconButton>
                            </Tooltip>
                        </Stack>

                        <Button
                            id={'btn-reset-local-app-data'}
                            variant={'outlined'}
                            color={'warning'}
                            onClick={() => setConfirmOpen(true)}
                            sx={{alignSelf: {xs: 'flex-start', sm: 'center'}, flexShrink: 0}}
                        >
                            {t('Clear Crqlar browser data')}
                        </Button>
                    </Stack>
                </CardContent>
            </Card>

            <Dialog
                open={confirmOpen}
                onClose={resetting ? undefined : () => setConfirmOpen(false)}
                maxWidth={'xs'}
                fullWidth
                aria-labelledby={'reset-local-app-data-title'}
            >
                <DialogTitle id={'reset-local-app-data-title'}>
                    {t('Clear Crqlar data from this browser')}
                </DialogTitle>

                <DialogContent>
                    <Stack gap={1.5}>
                        <Typography variant={'body1'} color={'text.secondary'}>
                            {t('This clears Crqlar data stored in this browser: local storage, session data, accessible cookies, app-managed caches, and local databases. You will be signed out and the page will reload.')}
                        </Typography>
                        <Typography variant={'body2'} fontWeight={600}>
                            {t('Your Crqlar account and server data will not be deleted.')}
                        </Typography>
                    </Stack>
                </DialogContent>

                <DialogActions>
                    <Button
                        onClick={() => setConfirmOpen(false)}
                        disabled={resetting}
                    >
                        {t('Cancel')}
                    </Button>
                    <LoadingButton
                        id={'btn-reset-local-app-data-confirm'}
                        variant={'contained'}
                        color={'warning'}
                        loading={resetting}
                        onClick={handleReset}
                    >
                        {t('Clear and reload')}
                    </LoadingButton>
                </DialogActions>
            </Dialog>
        </Stack>
    );
};

export default AccountSessionActions;
